import { isValid, parseISO } from 'date-fns';
import React, { FC } from 'react';
import styled from 'styled-components';
import { CycleOverviewViewModel } from '../../../common/model/type/hormone-summary-status';
import { UserCycle } from '../../../common/model/dto/user-cycle/user-cycle';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import ChartUtils from '../../../common/utils/services/chart-utils';
import { getCycleLengthStatus } from '../../../common/utils/services/hormone-summary-utils';
import OvSummaryStatus, {
  SummaryStatusPresentation,
} from '../molecules/OvSummaryStatus';
import {
  getCycleLengthPresentation,
  getMagnitudePresentation,
  getOvulationPresentation,
  getPhaseLengthPresentation,
  getVariabilityPresentation,
} from './hormone-summary-presentation';

interface OvCycleOverviewSummaryProps {
  overview: CycleOverviewViewModel;
  selectedCycle: UserCycle;
}

interface OverviewMetric {
  key: string;
  title: string;
  value: string | null;
  detail?: string;
  typicalRange?: string;
  presentation: SummaryStatusPresentation;
}

const formatNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);

const formatDays = (value: number | null): string | null => {
  if (value === null) {
    return null;
  }

  return `${formatNumber(value)} ${value === 1 ? 'day' : 'days'}`;
};

/**
 * Cycle Overview and Hormone Trends must never disagree on how long a cycle
 * is. Hormone Trends already derives its day count from `UserCycle.start_day`/
 * `end_day` (the cycle-list record); this recomputes the same way here rather
 * than trusting the hormone-summary API's own, independently-sourced
 * `cycle_length_days`, which can drift from it by a day for the same cycle.
 */
const getCorrectedCycleLength = (
  selectedCycle: UserCycle,
  fallback: number | null
): number | null => {
  const cycleStart = parseISO(selectedCycle.start_day);
  const cycleEnd = parseISO(selectedCycle.end_day);

  if (!isValid(cycleStart) || !isValid(cycleEnd)) {
    return fallback;
  }

  return ChartUtils.getWindowLength(cycleStart, cycleEnd);
};

const OvCycleOverviewSummary: FC<OvCycleOverviewSummaryProps> = ({
  overview,
  selectedCycle,
}) => {
  const magnitudePresentation = getMagnitudePresentation(
    overview.ovulationMagnitudeStatus
  );
  const cycleLengthDays = getCorrectedCycleLength(
    selectedCycle,
    overview.cycleLengthDays
  );
  const cycleLengthStatus = getCycleLengthStatus(cycleLengthDays);
  const metrics: OverviewMetric[] = [
    {
      key: 'ovulation',
      title: 'Ovulation',
      value:
        overview.lhSurgeCycleDay === null
          ? overview.ovulationStatus === 'anovulatory'
            ? 'No LH surge detected'
            : null
          : `LH surge detected on CD${overview.lhSurgeCycleDay}`,
      presentation: getOvulationPresentation(overview.ovulationStatus),
      detail:
        overview.lhSurgeCycleDay !== null
          ? `Magnitude: ${magnitudePresentation.label}`
          : undefined,
    },
    {
      key: 'follicular',
      title: 'Follicular phase length',
      value: formatDays(overview.follicularPhaseLengthDays),
      presentation: getPhaseLengthPresentation(
        overview.follicularPhaseLengthStatus,
        '10–17 days'
      ),
      typicalRange: 'Typical range: 10–17 days',
    },
    {
      key: 'luteal',
      title: 'Luteal phase length',
      value: formatDays(overview.lutealPhaseLengthDays),
      presentation: getPhaseLengthPresentation(
        overview.lutealPhaseLengthStatus,
        '12–16 days'
      ),
      typicalRange: 'Typical range: 12–16 days',
    },
    {
      key: 'cycle',
      title: 'Total cycle length',
      value: formatDays(cycleLengthDays),
      presentation: getCycleLengthPresentation(cycleLengthStatus),
      typicalRange: 'Typical range: 22–35 days',
    },
    {
      key: 'variability',
      title: 'Cycle-length variability',
      value: formatDays(overview.cycleLengthVariabilityDays),
      presentation: getVariabilityPresentation(
        overview.cycleLengthVariabilityStatus
      ),
    },
  ];

  return (
    <Section aria-labelledby="cycle-overview-heading">
      <SectionHeading id="cycle-overview-heading">
        Cycle overview
      </SectionHeading>
      <OverviewGrid>
        {metrics.map((metric) => (
          <MetricCard key={metric.key}>
            <MetricHeader>
              <MetricTitle>{metric.title}</MetricTitle>
            </MetricHeader>
            <StatusRow>
              <OvSummaryStatus
                presentation={metric.presentation}
                size="large"
              />
            </StatusRow>
            <MetricContent>
              <MetricValue $available={metric.value !== null}>
                <span>{metric.value ?? '\u00a0'}</span>
                {metric.detail && <MetricDetail>{metric.detail}</MetricDetail>}
              </MetricValue>
            </MetricContent>
            <TypicalRange $visible={metric.typicalRange !== undefined}>
              {metric.typicalRange ?? '\u00a0'}
            </TypicalRange>
          </MetricCard>
        ))}
      </OverviewGrid>
    </Section>
  );
};

export default OvCycleOverviewSummary;

const Section = styled.section`
  min-width: 0;
`;

const SectionHeading = styled.h3`
  margin: 0 0 0.75rem;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-rows: 1fr;
  gap: 0.75rem;

  @container hormone-summary (min-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container hormone-summary (min-width: 760px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @container hormone-summary (min-width: 1200px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`;

const MetricCard = styled.article`
  display: grid;
  grid-template-rows: 3rem 3rem minmax(3rem, auto) auto;
  align-content: stretch;
  min-width: 0;
  height: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.LARGE};
  background: ${Colours.WHITE};
`;

const MetricHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-width: 0;
  padding-bottom: 0.5rem;
  text-align: center;
`;

const MetricTitle = styled.h4`
  margin: 0;
  color: ${Colours.OV_PRUSSIAN_BLUE};
  font-size: ${Variables.fontSizes.MEDIUM};
  text-transform: uppercase;
  line-height: ${Variables.lineHeights.LARGE};
  transform: translateY(-0.25rem);
`;

const MetricContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  padding-top: 0.625rem;
  text-align: center;
`;

const MetricValue = styled.span<{ $available: boolean }>`
  display: block;
  min-width: 0;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  line-height: ${Variables.lineHeights.LARGE};
  overflow-wrap: anywhere;
  visibility: ${({ $available }) => ($available ? 'visible' : 'hidden')};
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 0;
  white-space: nowrap;
  transform: translateY(0.25rem);
`;

const MetricDetail = styled.span`
  display: block;
  color: ${Colours.OV_PRUSSIAN_BLUE};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: normal;
`;

const TypicalRange = styled.p<{ $visible: boolean }>`
  align-self: end;
  min-height: 2.25rem;
  margin: 0;
  padding-top: 0.5rem;
  border-top: 1px solid
    ${({ $visible }) => ($visible ? Colours.OV_BORDER_COLOR_V3 : 'transparent')};
  color: ${Colours.OV_PRUSSIAN_BLUE};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: ${Variables.lineHeights.LARGE};
  text-align: center;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;
